import React, { useState } from 'react';
import { Checkbox } from '@mui/material';
import { CreateChancel } from 'components';
import { goalTypes } from 'fragments';
import { Images, useModal } from 'utils';
import { RichTextElement } from '../../formBody/elements/richTextElement';
import { useMentionOptions } from '../../formBody/elements/useMentionOptions';
import { mapGoalBlocks, parseInlineFields, updateGoalBlock } from '../../../utils/inlineFields';
import { InlineFieldProperties } from './inlineFieldProperties';

export const goalBlockDesignModes = { GOAL: 'GOAL', TARGET: 'TARGET' };

const DESIGN_COPY = {
    [goalBlockDesignModes.GOAL]: {
        title: 'Goal design',
        description: "This layout repeats for every goal in the note. Place the target block where each goal's targets should appear.",
        save: 'Save Goal Design',
    },
    [goalBlockDesignModes.TARGET]: {
        title: 'Target design',
        description: 'This layout repeats for every picked target inside a goal.',
        save: 'Save Target Design',
    },
};

/**
 * Edits a Goal block's two designs. The goal design editor offers goal tokens
 * and the Target block; clicking that block switches to the target design
 * (target tokens too) and back. Save writes both; Cancel drops both.
 *
 * Both designs take inline fields (the Insert row: Input / Date / Select /
 * Checkbox / Radio). Clicking one opens its properties beside the editor. Each
 * picked goal (and target) gets its own copy of the fields to fill on the note.
 *
 * Rendered in the global modal, outside the form builder's context, so the
 * tokens come in as props.
 */
export const GoalBlockDesignModal = ({ block, initialMode, goalTokens, targetTokens, onSave }) => {
    const { close } = useModal();
    const [mode, setMode] = useState(initialMode);
    const [goalDesign, setGoalDesign] = useState(block?.goalDesign || '');
    const [targetDesign, setTargetDesign] = useState(block?.targetDesign || '');
    const [selectedFieldId, setSelectedFieldId] = useState(null);
    const copy = DESIGN_COPY[mode];
    const isGoal = mode === goalBlockDesignModes.GOAL;
    const design = isGoal ? goalDesign : targetDesign;
    const setDesign = isGoal ? setGoalDesign : setTargetDesign;
    const selectedField = selectedFieldId ? parseInlineFields(design).find((entry) => entry.id === selectedFieldId) : null;
    const switchMode = (next) => {
        setSelectedFieldId(null);
        setMode(next);
    };

    return (
        <div className={`goal-block-design-modal${selectedField ? ' goal-block-design-modal-with-field' : ''}`}>
            <p className="modal-header-title">{copy.title}</p>
            <p className="goal-block-design-desc">{copy.description}</p>
            {!isGoal && initialMode === goalBlockDesignModes.GOAL && (
                <button type="button" className="goal-block-design-back" onClick={() => switchMode(goalBlockDesignModes.GOAL)}>
                    <img src={Images.arrowRight} alt="" />
                    Goal design
                </button>
            )}
            <div className="goal-block-design-body">
                <div className="goal-block-design-editor" style={{ marginTop: 24 }}>
                    {/* Keyed by mode: each design gets its own editor. */}
                    <RichTextElement
                        key={mode}
                        value={design}
                        onChange={setDesign}
                        mentionOptions={isGoal ? goalTokens : targetTokens}
                        targetBlock={isGoal ? { onSelect: () => switchMode(goalBlockDesignModes.TARGET) } : undefined}
                        allowInlineFields
                        onSelectInlineField={setSelectedFieldId}
                        wrapperStyle={{ border: 'none' }}
                    />
                </div>
                {selectedField && (
                    <div className="goal-block-design-field">
                        <InlineFieldProperties
                            key={selectedField.id}
                            richText={{ value: design }}
                            entry={selectedField}
                            parentLabel={copy.title}
                            handleUpdate={(key, html) => key === 'value' && setDesign(html)}
                            onBack={() => setSelectedFieldId(null)}
                            onClose={() => setSelectedFieldId(null)}
                        />
                    </div>
                )}
            </div>
            <CreateChancel
                create={copy.save}
                chancel="Cancel"
                onClose={close}
                onCreate={() => {
                    onSave({ goalDesign, targetDesign });
                    close();
                }}
                buttonWidth="100%"
                cancelButtonWidth="100%"
            />
        </div>
    );
};

/** A design as one line of text for its card: tokens as written, markup dropped. */
const designSummary = (html) => {
    const text = String(html || '')
        .replace(/<div[^>]*data-target-block[^>]*>[\s\S]*?<\/div>/g, ' [Target block] ')
        .replace(/<\/p>/g, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
    return text || 'Empty — click to design';
};

const DesignCard = ({ title, subtitle, html, onClick, variant }) => (
    <button type="button" className={`goal-block-design-card goal-block-design-card-${variant}`} onClick={onClick}>
        <img src={Images.attributeGoal} alt="" />
        <span className="goal-block-design-card-text">
            <span className="goal-block-design-card-title">{title}</span>
            <span className="goal-block-design-card-subtitle">{subtitle}</span>
            <span className="goal-block-design-card-summary">{designSummary(html)}</span>
        </span>
        <img src={Images.arrowRight} alt="" className="goal-block-design-card-arrow" />
    </button>
);

const ToggleRow = ({ title, description, checked, onChange }) => (
    <div className="behavior-row">
        <div className="behavior-info">
            <span className="behavior-title">{title}</span>
            <span className="behavior-desc">{description}</span>
        </div>
        <label className="toggle-switch">
            <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
            <span className="slider"></span>
        </label>
    </div>
);

/**
 * Properties of a Goal block inside a RICH_TEXT (progress reports). Each change
 * rewrites the block's div in the text (`updateGoalBlock`).
 */
export const GoalBlockProperties = ({ richText, entry, handleUpdate, onBack, onClose }) => {
    const { id, block } = entry;
    const { open } = useModal();
    const goalTokens = useMentionOptions({ goalMode: true });
    const targetTokens = useMentionOptions({ goalMode: true, targetMode: true });

    const update = (patch) => handleUpdate('value', updateGoalBlock(richText?.value, id, { block: { ...block, ...patch } }));
    const updateProperty = (key, value) => update({ properties: { ...(block?.properties || {}), [key]: value } });

    // No types saved means every type. At least one stays checked — a block for no type could never list a goal.
    const allTypeIds = goalTypes.map((opt) => opt.id);
    const selectedTypes = block?.goalTypes?.length ? block.goalTypes : allTypeIds;
    const toggleType = (type) => {
        const isSelected = selectedTypes.includes(type);
        if (isSelected && selectedTypes.length === 1) return;
        const next = isSelected ? selectedTypes.filter((t) => t !== type) : [...selectedTypes, type];
        update({ goalTypes: next.length === allTypeIds.length ? [] : allTypeIds.filter((t) => next.includes(t)) });
    };

    const openDesign = (mode) =>
        open(
            <GoalBlockDesignModal
                block={block}
                initialMode={mode}
                goalTokens={goalTokens}
                targetTokens={targetTokens}
                onSave={(designs) => update(designs)}
            />,
        );

    const handleDelete = () => {
        handleUpdate('value', mapGoalBlocks(richText?.value, (other) => (other.id === id ? '' : undefined)));
        onBack();
    };

    return (
        <div className="properties-wrapper">
            <div className="properties-header">
                <h3>Properties</h3>
                <button className="reset-btn-by-default" onClick={onClose}>
                    <img src={Images.closeSingleVector} alt="close" style={{ width: 18, height: 18 }} />
                </button>
            </div>
            <div className="inline-field-breadcrumb">
                <button type="button" className="inline-field-breadcrumb-back" onClick={onBack}>
                    <img src={Images.arrowRight} alt="" />
                    Rich Text
                </button>
                <span className="inline-field-breadcrumb-sep">/</span>
                <img src={Images.attributeGoal} alt="" className="inline-field-breadcrumb-icon" />
                <span className="inline-field-breadcrumb-name">Goal Block</span>
            </div>
            <div className="properties-divider" />

            <div className="properties-section">
                <div className="section-header">
                    <img src={Images.contentIcon} alt="content" />
                    <span>CONTENT</span>
                </div>
                <div className="props-input-group">
                    <div className="props-label">Label</div>
                    <input type="text" className="custom-input" value={block?.label || ''} onChange={(e) => update({ label: e.target.value })} />
                </div>
                <div className="props-label">Goal Types</div>
                {goalTypes.map((opt) => {
                    const checked = selectedTypes.includes(opt.id);
                    return (
                        <div key={opt.id} className="behavior-row">
                            <div className="behavior-info">
                                <span className="behavior-title">{opt.label}</span>
                            </div>
                            <Checkbox
                                checked={checked}
                                disabled={checked && selectedTypes.length === 1}
                                onChange={() => toggleType(opt.id)}
                                style={{ padding: 0, color: checked ? '#0C66E4' : '#D0D5DD' }}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="properties-divider" />

            <div className="properties-section">
                <div className="section-header">
                    <img src={Images.layoutIcon} alt="design" />
                    <span>DESIGN</span>
                </div>
                <DesignCard
                    variant="goal"
                    title="Goal design"
                    subtitle="Repeats for each goal"
                    html={block?.goalDesign}
                    onClick={() => openDesign(goalBlockDesignModes.GOAL)}
                />
                <DesignCard
                    variant="target"
                    title="Target design"
                    subtitle="Repeats for each target inside a goal"
                    html={block?.targetDesign}
                    onClick={() => openDesign(goalBlockDesignModes.TARGET)}
                />
            </div>
            <div className="properties-divider" />

            <div className="properties-section">
                <div className="section-header">
                    <img src={Images.behaivorIcon} alt="behavior" />
                    <span>BEHAVIOR</span>
                </div>
                <ToggleRow
                    title="Required"
                    description="Staff must pick at least one goal"
                    checked={block?.properties?.required}
                    onChange={(value) => updateProperty('required', value)}
                />
                <ToggleRow
                    title="Read Only"
                    description="Staff cannot change the goals"
                    checked={block?.properties?.readOnly}
                    onChange={(value) => updateProperty('readOnly', value)}
                />
            </div>
            <div className="properties-divider" />

            <div className="properties-section">
                <div className="section-header">
                    <img src={Images.behaivorIcon} alt="actions" />
                    <span>ACTIONS</span>
                </div>
                <div className="properties-actions-row">
                    <button className="properties-action-btn delete" onClick={handleDelete}>
                        <img src={Images.trash} alt="delete" />
                        <span>Delete</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
